'use client';

import { BoxScrolled, usePortalContext } from '@cloai/uikit';
import { useTranslations } from 'next-intl';
import { SyntheticEvent, FC, memo } from 'react';

import { PORTALS } from '@/.constants/portals';
import AiVideosIcon from '@/public/icons/ai_videos.svg';
import { SeeAllButton } from '@/src/components/SeeAllButton';
import { useVitrinaPosition } from '@/src/hooks/useVitrinaPosition';
import {
  useProfileContext,
  useStyleContext,
  useGTMContext,
  TActualItem,
} from '@/src/providers';
import { getBlockName } from '@/src/utils/getBlockName';
import { getImageByGender } from '@/src/utils/getImageByGender';

import { PackPreview } from './PackPreview';
const styles = {
  wrapper: 'flex flex-col gap-3',
  line: 'flex items-center justify-between',
  lineInner: 'flex items-center gap-2',
  lineTitle: 'text-lg font-bold',
  lineIcon: 'w-5 h-5',
  lineCount: 'text-sm font-medium text-label-secondary',
  head: 'flex items-center justify-between mb-3',
  name: 'text-sm font-semibold',
};

export const VideoVeo3: FC = memo(() => {
  const t = useTranslations('MenuPage');
  const profile = useProfileContext()!;
  const { openPortal } = usePortalContext();
  const {
    genderMode,
    setPortalSeeAllMode,
    videoVeo3,
    setPrevPortal,
    changeActiveStyle,
  } = useStyleContext();
  const sendGTM = useGTMContext();
  const vitrinaPosition = useVitrinaPosition('videoVeo3');

  const handleClickSeeAll = () => {
    setPrevPortal('main');
    setPortalSeeAllMode('videoVeo3');
    openPortal(PORTALS.SeeAllImages);
  };

  const handleClickImage = (event: SyntheticEvent) => {
    const styleId = event.currentTarget.getAttribute('data-style-id');
    const currentIndex = event.currentTarget.getAttribute('data-style-ind');
    let position;

    if (styleId) {
      changeActiveStyle(styleId, 'videoVeo3');
    }

    if (currentIndex) {
      position = Number(currentIndex);
    }

    const currentStyle = videoVeo3?.find((item) => item.id === styleId);

    sendGTM({
      event: 'menu_selection',
      type: currentStyle?.__typename!,
      block: getBlockName('videoVeo3'),
      category_name: null,
      name: (currentStyle as TActualItem)?.name['en'],
      category_position: position,
      vitrina_position: vitrinaPosition,
      gender: genderMode,
    });

    setPrevPortal('main');
    setPortalSeeAllMode('videoVeo3');
    openPortal(PORTALS.GenerateImage);
  };

  if (!videoVeo3.length) {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.line}>
        <div className={styles.lineInner}>
          <AiVideosIcon className={styles.lineIcon} />
          <div className={styles.lineTitle}>{t('videoVeo3')}</div>
        </div>
        <SeeAllButton onClick={handleClickSeeAll} />
      </div>
      <BoxScrolled direction="horizontal" hideScrollbar gap={8} offsetOut={20}>
        {videoVeo3?.map((item, i) => (
          <PackPreview
            key={item.id}
            id={item.id}
            index={i}
            isPopular={item.isPopular}
            isNew={item.isNew}
            name={item.name[profile?.lang]}
            image={getImageByGender(item, genderMode) ?? ''}
            handleClickImage={handleClickImage}
            isVideo={true}
            isVeo3={true}
            limitedUntil={item.limitedUntil}
          />
        ))}
      </BoxScrolled>
    </div>
  );
});

VideoVeo3.displayName = 'VideoVeo3';
